/**
 * Payment Accounts Management Page
 * Admin page for managing payment account details
 */

import React, { useState } from "react";
import Button from "../../components/ui/Button";
// Add missing imports
import Icon from "../../components/AppIcon";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import { useToast } from "../../contexts/ToastContext";
import {
	useCreatePaymentAccountMutation,
	useDeletePaymentAccountMutation,
	useListPaymentAccountsQuery,
	useUpdatePaymentAccountMutation,
	type PaymentAccount,
} from "../../store/api/paymentAccountsApi";
import CreatePaymentAccountModal from "./components/CreatePaymentAccountModal";
import EditPaymentAccountModal from "./components/EditPaymentAccountModal";
import PaymentAccountTable from "./components/PaymentAccountTable";

const PaymentAccountsManagement: React.FC = () => {
	const { success, error: showError } = useToast();
	const [page, setPage] = useState(1);
	const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(
		undefined,
	);
	const [searchQuery, setSearchQuery] = useState("");
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState<PaymentAccount | null>(
		null,
	);
	const [accountToDelete, setAccountToDelete] = useState<PaymentAccount | null>(
		null,
	);

	const {
		data: accountsData,
		isLoading,
		error,
		refetch,
	} = useListPaymentAccountsQuery({
		page,
		page_size: 20,
		is_active: isActiveFilter,
		search: searchQuery || undefined,
		ordering: "-created_at",
	});

	const [createPaymentAccount, { isLoading: isCreating }] =
		useCreatePaymentAccountMutation();
	const [updatePaymentAccount, { isLoading: isUpdating }] =
		useUpdatePaymentAccountMutation();
	const [deletePaymentAccount, { isLoading: isDeleting }] =
		useDeletePaymentAccountMutation();

	const handleCreate = () => {
		setSelectedAccount(null);
		setIsCreateModalOpen(true);
	};

	const handleEdit = (account: PaymentAccount) => {
		setSelectedAccount(account);
		setIsEditModalOpen(true);
	};

	const handleDelete = (account: PaymentAccount) => {
		setAccountToDelete(account);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!accountToDelete) return;

		try {
			await deletePaymentAccount(accountToDelete.id).unwrap();
			success("Payment account deleted successfully!");
			setIsDeleteModalOpen(false);
			setAccountToDelete(null);
			refetch();
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to delete payment account. Please try again.",
			);
		}
	};

	const handleCreateSubmit = async (data: any) => {
		try {
			await createPaymentAccount(data).unwrap();
			success("Payment account created successfully!");
			setIsCreateModalOpen(false);
			refetch();
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to create payment account. Please try again.",
			);
		}
	};

	const handleEditSubmit = async (data: any) => {
		if (!selectedAccount) return;

		try {
			await updatePaymentAccount({ id: selectedAccount.id, data }).unwrap();
			success("Payment account updated successfully!");
			setIsEditModalOpen(false);
			setSelectedAccount(null);
			refetch();
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to update payment account. Please try again.",
			);
		}
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
			{/* Page Header */}
			<div className="mb-6">
				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
							Payment Accounts
						</h1>
						<p className="text-muted-foreground mt-1">
							Manage bank account details for payment collection
						</p>
					</div>
					<Button onClick={handleCreate}>
						<Icon name="Plus" size={16} className="mr-2" />
						Add Payment Account
					</Button>
				</div>
			</div>

			{/* Filters */}
			<div className="bg-card border border-border rounded-lg p-4 sm:p-6 mb-6">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className="md:col-span-2">
						<Input
							label="Search"
							placeholder="Search by account name, number, or bank..."
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
								setPage(1);
							}}
						/>
					</div>
					<Select
						label="Status"
						value={
							isActiveFilter === undefined
								? "all"
								: isActiveFilter
								? "active"
								: "inactive"
						}
						onValueChange={(value) => {
							const selectedValue = Array.isArray(value) ? value[0] : value;
							setIsActiveFilter(
								String(selectedValue) === "all"
									? undefined
									: String(selectedValue) === "active",
							);
							setPage(1);
						}}
						options={[
							{ value: "all", label: "All" },
							{ value: "active", label: "Active" },
							{ value: "inactive", label: "Inactive" },
						]}
					/>
				</div>
			</div>

			{/* Accounts Table */}
			<div className="mt-6">
				<PaymentAccountTable
					accounts={accountsData?.results || []}
					isLoading={isLoading}
					onEdit={handleEdit}
					onDelete={handleDelete}
				/>
			</div>

			{/* Pagination */}
			{accountsData && accountsData.count > 0 && (
				<div className="mt-6 flex items-center justify-between">
					<p className="text-sm text-muted-foreground">
						Showing {(page - 1) * 20 + 1} to{" "}
						{Math.min(page * 20, accountsData.count)} of {accountsData.count}{" "}
						accounts
					</p>
					<div className="flex items-center space-x-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={page === 1 || isLoading}
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPage((p) => p + 1)}
							disabled={
								!accountsData.next ||
								isLoading ||
								page * 20 >= accountsData.count
							}
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			<CreatePaymentAccountModal
				isOpen={isCreateModalOpen}
				onClose={() => setIsCreateModalOpen(false)}
				onSubmit={handleCreateSubmit}
				isLoading={isCreating}
			/>

			{/* Edit Modal */}
			{selectedAccount && (
				<EditPaymentAccountModal
					account={selectedAccount}
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setSelectedAccount(null);
					}}
					onSubmit={handleEditSubmit}
					isLoading={isUpdating}
				/>
			)}

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setAccountToDelete(null);
				}}
				onConfirm={handleConfirmDelete}
				title="Delete Payment Account"
				message={`Are you sure you want to delete the payment account "${accountToDelete?.account_name}"? This action cannot be undone.`}
				confirmButtonText="Delete"
				isDestructive={true}
				isLoading={isDeleting}
			/>
		</div>
		</div>
	);
};

export default PaymentAccountsManagement;
